import { useMailTheme } from "@/hooks/useMailTheme";
import {
  MAIL_ACCENT_IDS,
  MAIL_ACCENT_PRESETS,
  type MailAccentId,
} from "@/lib/theme/mailAccent";
import { cn } from "@/lib/cn";

type Props = {
  onClose?: () => void;
  compact?: boolean;
};

/** Panneau thème réutilisable (menu burger ou popover). */
export function MailThemePanel({ onClose, compact = false }: Props) {
  const { theme, accent, setTheme, setAccent } = useMailTheme();
  const isDark = theme === "dark";

  return (
    <div className={compact ? "space-y-3" : ""}>
      {!compact ? (
        <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
          Thème
        </p>
      ) : null}

      <p className="mb-1.5 text-[11px] font-medium text-[var(--mail-text)]">Apparence</p>
      <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-[var(--mail-bg)] p-0.5">
        <button
          type="button"
          className={cn(
            "rounded-md px-2 py-1.5 text-xs font-medium transition",
            !isDark
              ? "bg-[var(--mail-surface)] text-[var(--mail-text)] shadow-sm"
              : "text-[var(--mail-muted)] hover:text-[var(--mail-text)]"
          )}
          onClick={() => {
            if (isDark) setTheme("light");
          }}
        >
          Clair
        </button>
        <button
          type="button"
          className={cn(
            "rounded-md px-2 py-1.5 text-xs font-medium transition",
            isDark
              ? "bg-[var(--mail-surface-elevated)] text-[var(--mail-text)] shadow-sm ring-1 ring-[var(--mail-border-soft)]"
              : "text-[var(--mail-muted)] hover:text-[var(--mail-text)]"
          )}
          onClick={() => {
            if (!isDark) setTheme("dark");
          }}
        >
          Sombre
        </button>
      </div>

      <p className="mb-2 text-[11px] font-medium text-[var(--mail-text)]">Couleur d&apos;accent</p>
      <div
        className="grid grid-cols-3 gap-1.5 sm:grid-cols-4"
        role="listbox"
        aria-label="Couleur d'accent"
      >
        {MAIL_ACCENT_IDS.map((id) => {
          const preset = MAIL_ACCENT_PRESETS[id];
          const selected = accent === id;
          return (
            <button
              key={id}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={preset.label}
              className={cn(
                "flex flex-col items-center gap-1.5 rounded-lg border px-1.5 py-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-1",
                selected
                  ? "border-[var(--mail-accent)] bg-[var(--mail-accent-soft)] shadow-sm"
                  : "border-[var(--mail-border-soft)] bg-[var(--mail-surface)] hover:border-[var(--mail-border)] hover:bg-[var(--mail-surface-hover)]"
              )}
              onClick={() => {
                setAccent(id as MailAccentId);
              }}
            >
              <span
                className={cn(
                  "h-6 w-6 shrink-0 rounded-full border border-black/10 shadow-sm dark:border-white/15",
                  selected && "ring-2 ring-[var(--mail-accent)]/35 ring-offset-1 ring-offset-[var(--mail-surface)]"
                )}
                style={{ backgroundColor: preset.swatch }}
                aria-hidden
              />
              <span
                className={cn(
                  "text-[10px] font-medium leading-none",
                  selected ? "text-[var(--mail-accent)]" : "text-[var(--mail-muted)]"
                )}
              >
                {preset.label}
              </span>
            </button>
          );
        })}
      </div>
      {onClose ? (
        <button
          type="button"
          className="mt-3 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-2.5 py-1.5 text-xs font-semibold text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)]"
          onClick={onClose}
        >
          Fermer
        </button>
      ) : null}
    </div>
  );
}
